//* Vue
import { SetupContext, computed, nextTick, ref } from "vue";
//* 组件
import type UiSimplebar from "@various/components/simplebar";
//* 组件属性
import type { ModulePickerEmits, ModuleData, ModuleDay } from "../../../src/types";
import { analyzes } from "../../../src/utils";

export const useComposable = (emits: SetupContext<typeof ModulePickerEmits>["emit"]) => {
    //* 响应式变量
    const refs = {
        end: ref<Date>(),
        date: ref<Date>(new Date()),
        days: ref<ModuleDay[]>([]),
        start: ref<Date>(),
        finish: ref<boolean>(false),
    };

    //* 节点列表
    const nodes = {
        hourser: ref<InstanceType<typeof UiSimplebar>>(),
        minuteser: ref<InstanceType<typeof UiSimplebar>>(),
        secondser: ref<InstanceType<typeof UiSimplebar>>(),
    };

    //* 计算属性
    const cDate = computed(() => (refs.finish.value ? refs.end.value : refs.start.value));
    const computeds = {
        cSeconds: computed(() => (cDate.value ? cDate.value.getSeconds() : -1)),
        cMinutes: computed(() => (cDate.value ? cDate.value.getMinutes() : -1)),
        cHours: computed(() => (cDate.value ? cDate.value.getHours() : -1)),
        cTime: computed(() => {
            if (!cDate.value) return "";
            else {
                const cHours = cDate.value.getHours().toString().padStart(2, "0");
                const cMinutes = cDate.value.getMinutes().toString().padStart(2, "0");
                const cSeconds = cDate.value.getSeconds().toString().padStart(2, "0");

                return `${cHours}:${cMinutes}:${cSeconds}`;
            }
        }),
    };

    //* 函数列表
    const methods = {
        //* 更新函数
        date: (data: { year?: number; month?: number }) => emits("update", data),
        //* 初始化函数
        init: (data: ModuleData) => {
            //* 数据初始化
            refs.end.value = analyzes.date(data.end);
            refs.start.value = analyzes.date(data.start);

            //* 获取日期列表与时间定位
            methods.update(data.date);
            methods.get_time_site();

            //* 检测当前编辑的状态
            if (data.event) {
                const node = data.event?.target as HTMLElement | null;
                const name = node?.getAttribute("aria-name");
                if (name == "start") refs.finish.value = false;
                else if (name == "end") refs.finish.value = true;
                else {
                    const size = node?.offsetWidth || 0;
                    if (data.event.offsetX > size / 2) refs.finish.value = true;
                    else {
                        refs.finish.value = false;
                    }
                }

                if (refs.finish.value && refs.end.value) emits("update", { year: refs.end.value.getFullYear(), month: refs.end.value.getMonth() });
                else if (!refs.finish.value && refs.start.value) {
                    emits("update", { year: refs.start.value.getFullYear(), month: refs.start.value.getMonth() });
                }
            }
        },

        //* 状态更新
        update: (date: Date) => {
            //* 数据更新
            refs.days.value = [];
            refs.date.value = date;
            //* 获取日份列表的开始日期
            const d = analyzes.get_month_start_date(refs.date.value);
            for (let i = 0; i < 42; i++) {
                const current = new Date(d.getFullYear(), d.getMonth(), d.getDate() + i);
                refs.days.value.push({
                    day: current.getDate(),
                    date: analyzes.string("date", current),
                    year: current.getFullYear(),
                    month: current.getMonth(),
                });
            }
        },

        //* 选择函数
        select: (data: { date?: ModuleDay; hours?: number; minutes?: number; seconds?: number }) => {
            //* 模拟数据
            const simulation: { main?: Date; vice?: Date } = {};
            if (refs.finish.value) {
                simulation.main = refs.end.value && new Date(refs.end.value);
                simulation.vice = refs.start.value && new Date(refs.start.value);
            } else {
                simulation.main = refs.start.value && new Date(refs.start.value);
                simulation.vice = refs.end.value && new Date(refs.end.value);
            }

            //* 日期修改
            if (data.date) {
                if (!simulation.main) simulation.main = new Date(data.date.year, data.date.month, data.date.day);
                else {
                    simulation.main.setFullYear(data.date.year);
                    simulation.main.setMonth(data.date.month);
                    simulation.main.setDate(data.date.day);
                }
            } else {
                //* 时间修改
                if (!simulation.main) {
                    simulation.main = new Date();
                    simulation.main.setHours(0);
                    simulation.main.setMinutes(0);
                    simulation.main.setSeconds(0);
                }

                //* 修改时
                if (data.hours != undefined) simulation.main.setHours(data.hours);
                //* 修改分
                if (data.minutes != undefined) simulation.main.setMinutes(data.minutes);
                //* 修改秒
                if (data.seconds != undefined) {
                    simulation.main.setSeconds(data.seconds);
                }
            }

            //* 获取正确顺序的数据
            const result = analyzes.get_correct_date({ start: simulation.main, end: simulation.vice });
            //* 数据缓存
            refs.finish.value = result.end == simulation.main;
            refs.start.value = result.start;
            refs.end.value = result.end;

            //* 时间定位
            methods.get_time_site();

            //* 事件响应
            emits("message", result);
        },

        //* 确认函数
        confirm: () => {
            //* 事件响应
            emits("change", { start: refs.start.value, end: refs.end.value });
            if (refs.start.value && !refs.end.value) {
                refs.finish.value = true;
            } else if (refs.end.value && !refs.start.value) {
                refs.finish.value = false;
            }
        },

        //* 获取时间位置
        get_time_site: () => {
            nextTick(() => {
                const nHours = nodes.hourser.value?.$el.querySelector(".ui-candidate.ui-active");
                const nMinutes = nodes.minuteser.value?.$el.querySelector(".ui-candidate.ui-active");
                const nSeconds = nodes.secondser.value?.$el.querySelector(".ui-candidate.ui-active");
                if (!nHours) nodes.hourser.value?.move(0);
                else {
                    nodes.hourser.value?.move(nHours.offsetTop - 4);
                }

                if (!nMinutes) nodes.minuteser.value?.move(0);
                else {
                    nodes.minuteser.value?.move(nMinutes.offsetTop - 4);
                }

                if (!nSeconds) nodes.secondser.value?.move(0);
                else {
                    nodes.secondser.value?.move(nSeconds.offsetTop - 4);
                }
            });
        },

        //* 检测日期状态
        get_day_status: (data: ModuleDay) => {
            //* 数据初始化
            const result: string[] = [];
            //* 检测日期是否为当前月份的日期
            if (refs.date.value?.getMonth() != data.month) {
                result.push("ui-other-status");
            }

            //* 检测日期是否被选中
            if (cDate.value?.getDate() == data.day && cDate.value?.getFullYear() == data.year && cDate.value?.getMonth() == data.month) {
                result.push("ui-active");
            }

            return result.join(" ");
        },
    };

    return { refs, nodes, methods, computeds };
};
